import { DocumentType, ResultStatus, RunStatus } from "@repo/shared/workflow";
import { relations } from "drizzle-orm";
import {
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  real,
  text,
  timestamp,
  unique,
  uuid,
} from "drizzle-orm/pg-core";
import { createSelectSchema } from "drizzle-zod";

/** Helpers */
const timestampsDef = {
  createdAt: timestamp().defaultNow().notNull(),
  updatedAt: timestamp({ mode: "date", precision: 3 })
    .defaultNow()
    .notNull()
    .$onUpdateFn(() => new Date()),
};

const orgIdDef = uuid()
  .notNull()
  .references(() => organizations.id, {
    onDelete: "cascade",
  });

/** Enums */
export const resultStatusEnum = pgEnum("result_status", [
  ResultStatus.WAITING,
  ResultStatus.RUNNING,
  ResultStatus.COMPLETED,
  ResultStatus.FAILED,
]);

export const runStatusEnum = pgEnum("run_status", [
  RunStatus.PENDING,
  RunStatus.PROCESSING,
  RunStatus.REVIEW,
  RunStatus.COMPLETED,
  RunStatus.FAILED,
]);

export const documentTypeEnum = pgEnum("document_type", [
  DocumentType.CSV,
  DocumentType.EMAIL,
  DocumentType.JSON,
  DocumentType.PDF,
  DocumentType.TXT,
  DocumentType.XLSX,
]);

// Need to export enums for Drizzle to pick them up
export { ResultStatus, RunStatus };

/* Organizations and projects are the core tables that most other tables are connected to */
export const organizations = pgTable("organizations", {
  id: uuid().defaultRandom().primaryKey(),
  name: text().notNull(),
  slug: text().notNull(),
  stytchId: text().notNull(),
  ...timestampsDef,
});

export const organizationRelations = relations(organizations, ({ many }) => ({
  workflows: many(workflows),
}));

/* Workflows define the core data model in each project */
export const workflows = pgTable("workflows", {
  id: uuid().defaultRandom().primaryKey(),
  name: text().notNull(),
  orgId: orgIdDef,
  deletedAt: timestamp(),
  ...timestampsDef,
});

export const workflowVersions = pgTable(
  "workflow_versions",
  {
    id: uuid().defaultRandom().primaryKey(),
    workflowId: uuid()
      .notNull()
      .references(() => workflows.id, { onDelete: "cascade" }),
    published: boolean().notNull().default(false),
    orgId: orgIdDef,
    ...timestampsDef,
  },
  (workflowVersions) => {
    return [
      index("workflow_versions_workflow_id_idx").on(
        workflowVersions.workflowId,
      ),
    ];
  },
);

export const workflowsRelations = relations(workflows, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [workflows.orgId],
    references: [organizations.id],
  }),
  versions: many(workflowVersions),
  tasks: many(tasks),
  documents: many(workflowDocuments),
}));

export const workflowVersionsRelations = relations(
  workflowVersions,
  ({ one, many }) => ({
    workflow: one(workflows, {
      fields: [workflowVersions.workflowId],
      references: [workflows.id],
    }),
    nodes: many(nodesTable),
    org: one(organizations, {
      fields: [workflowVersions.orgId],
      references: [organizations.id],
    }),
  }),
);

const workflowDef = {
  workflowId: uuid()
    .notNull()
    .references(() => workflows.id, {
      onDelete: "cascade",
    }),
  workflowVersionId: uuid()
    .notNull()
    .references(() => workflowVersions.id, {
      onDelete: "cascade",
    }),
};

export const nodesTable = pgTable(
  "nodes",
  {
    id: uuid().notNull(),
    data: jsonb().notNull(),
    orgId: orgIdDef,
    ...workflowDef,
    ...timestampsDef,
  },
  (table) => {
    return [
      primaryKey({ columns: [table.id, table.workflowVersionId] }),
      index("nodes_org_id_idx").on(table.orgId),
      index("nodes_workflow_id_idx").on(table.workflowId),
    ];
  },
);

export const nodesRelations = relations(nodesTable, ({ many, one }) => ({
  workflowVersion: one(workflowVersions, {
    fields: [nodesTable.workflowVersionId],
    references: [workflowVersions.id],
  }),
}));

/* Tasks are instances of a workflow */
export const tasks = pgTable(
  "tasks",
  {
    id: uuid().defaultRandom().primaryKey(),
    name: text().notNull(),
    orgId: orgIdDef,
    ...workflowDef,
    ...timestampsDef,
  },
  (tasks) => {
    return [
      // tasks are unique by name across the workflow
      unique("tasks_name_workflow_id_idx").on(tasks.name, tasks.workflowId),

      index("tasks_org_id_idx").on(tasks.orgId),
      index("tasks_workflow_id_idx").on(tasks.workflowId),
      index("tasks_workflow_version_id_idx").on(tasks.workflowVersionId),
    ];
  },
);

// A task can be run multiple times
export const runs = pgTable(
  "runs",
  {
    id: uuid().defaultRandom().primaryKey(),
    taskId: uuid()
      .notNull()
      .references(() => tasks.id, { onDelete: "cascade" }),
    finishedAt: timestamp(),
    status: runStatusEnum("status")
      .default(RunStatus.PENDING)
      .default(RunStatus.PENDING),
    orgId: orgIdDef,
    ...workflowDef,
    ...timestampsDef,
  },
  (runs) => {
    return [index("runs_task_id_idx").on(runs.taskId)];
  },
);

export const tasksRelations = relations(tasks, ({ many, one }) => ({
  runs: many(runs),
  workflow: one(workflows, {
    fields: [tasks.workflowId],
    references: [workflows.id],
  }),
  documents: many(taskDocuments),
}));

export const runsRelations = relations(runs, ({ one, many }) => ({
  task: one(tasks, {
    fields: [runs.taskId],
    references: [tasks.id],
  }),
  workflowVersion: one(workflowVersions, {
    fields: [runs.workflowVersionId],
    references: [workflowVersions.id],
  }),
  results: many(resultsTable),
}));

// Results are the output of a node run
export const resultsTable = pgTable(
  "results",
  {
    id: uuid().defaultRandom().primaryKey(),
    runId: uuid()
      .notNull()
      .references(() => runs.id, { onDelete: "cascade" }),
    output: jsonb(),
    orgId: orgIdDef,
    ...workflowDef,
    ...timestampsDef,
  },
  (results) => {
    return [
      index("results_run_id_idx").on(results.runId),
      index("results_workflow_id_idx").on(results.workflowId),
      index("results_workflow_version_id_idx").on(results.workflowVersionId),
    ];
  },
);

export const resultsRelations = relations(resultsTable, ({ one }) => ({
  run: one(runs, {
    fields: [resultsTable.runId],
    references: [runs.id],
  }),
}));

/*
taskDocuments are attached to a specific task

TODO: rename taskDocuments to taskFiles to be less confusing with `documents`
*/
export const taskDocuments = pgTable("task_documents", {
  id: uuid().defaultRandom().primaryKey(),
  fileName: text().notNull(),

  // TODO: enforce this as a documentTypeEnum
  fileType: text().notNull(),

  /** The raw file from the upload */
  storagePath: text().notNull(),

  /** The storage path for the PDF version of the file, if the file isn't already a PDF */
  pdfStoragePath: text(),

  /** The storage path for the textract/OCR results, broken up into citable chunks */
  contentStoragePath: text(),

  /** The job ID for the textract/OCR job */
  textractJobId: text(),

  taskId: uuid()
    .notNull()
    .references(() => tasks.id, { onDelete: "cascade" }),
  orgId: orgIdDef,
  ...workflowDef,
  ...timestampsDef,
});

/* workflowDocuments are used by various parts of the workflow */
export const workflowDocuments = pgTable("workflow_documents", {
  id: uuid().defaultRandom().primaryKey(),
  fileName: text().notNull(),
  fileType: text().notNull(),
  storagePath: text().notNull(),
  orgId: orgIdDef,
  ...workflowDef,
  ...timestampsDef,
});

export const workflowDocumentRelations = relations(
  workflowDocuments,
  ({ one }) => ({
    workflow: one(workflows, {
      fields: [workflowDocuments.workflowId],
      references: [workflows.id],
    }),
  }),
);

export const taskDocumentRelations = relations(
  taskDocuments,
  ({ many, one }) => ({
    citations: many(citations),
    task: one(tasks, {
      fields: [taskDocuments.taskId],
      references: [tasks.id],
    }),
  }),
);

export const citations = pgTable("citations", {
  id: uuid().defaultRandom().primaryKey(),
  text: text().notNull(),
  left: real().notNull(),
  top: real().notNull(),
  width: real().notNull(),
  height: real().notNull(),
  page: integer().notNull(),
  documentId: uuid()
    .notNull()
    .references(() => taskDocuments.id, { onDelete: "cascade" }),
  orgId: orgIdDef,
  ...timestampsDef,
});

export const citationsRelations = relations(citations, ({ one }) => ({
  document: one(taskDocuments, {
    fields: [citations.documentId],
    references: [taskDocuments.id],
  }),
}));

/* Select types */
export type OrganizationSelect = typeof organizations.$inferSelect;
export type WorkflowSelect = typeof workflows.$inferSelect;
export type WorkflowVersionSelect = typeof workflowVersions.$inferSelect;
export type TaskDocumentSelect = typeof taskDocuments.$inferSelect;
export type TaskDocumentInsert = typeof taskDocuments.$inferInsert;
export type ResultSelect = typeof resultsTable.$inferSelect;
export type NodeSelect = typeof nodesTable.$inferSelect;
export type OrgSelect = typeof organizations.$inferSelect;
export type TaskSelect = typeof tasks.$inferSelect;
export type WorkflowDocumentSelect = typeof workflowDocuments.$inferSelect;
export type CitationSelect = typeof citations.$inferSelect;

/* Insert types */
export type NodeInsert = typeof nodesTable.$inferInsert;
export type ResultInsert = typeof resultsTable.$inferInsert;
export type OrgInsert = typeof organizations.$inferInsert;
export type CitationInsert = typeof citations.$inferInsert;

/* Select zod schemas */
export const selectOrgSchema = createSelectSchema(organizations);
export const selectWorkflowSchema = createSelectSchema(workflows);
export const selectWorkflowVersionSchema = createSelectSchema(workflowVersions);
export const selectTaskSchema = createSelectSchema(tasks);
export const selectRunSchema = createSelectSchema(runs);
export const selectTaskDocumentSchema = createSelectSchema(taskDocuments);
export const selectWorkflowDocumentSchema =
  createSelectSchema(workflowDocuments);
export const selectCitationSchema = createSelectSchema(citations);
